import type { PdfActionDetailConfig } from '@/domains/export/pdfActionDetail.entity';
import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from '@react-pdf/renderer';

interface PdfActionDetailDocumentProps {
  config: PdfActionDetailConfig;
  author: string;
  generatedBy: string;
  logo?: string;
}

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 11,
    fontFamily: 'Helvetica',
    color: '#333333',
    backgroundColor: '#FFFFFF',
  },
  header: {
    marginBottom: 25,
    borderBottom: '3 solid #E91E63',
    paddingBottom: 15,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logo: {
    width: 70,
    height: 70,
    objectFit: 'contain',
  },
  headerText: {
    flex: 1,
    marginLeft: 15,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#E91E63',
    marginBottom: 5,
  },
  subtitle: {
    fontSize: 13,
    color: '#666666',
    marginBottom: 3,
  },
  metadata: {
    fontSize: 9,
    color: '#999999',
    marginTop: 5,
  },
  content: {
    marginTop: 20,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#E91E63',
    marginBottom: 15,
    marginTop: 10,
    borderBottom: '1 solid #EEEEEE',
    paddingBottom: 5,
  },
  fieldRow: {
    flexDirection: 'row',
    marginBottom: 12,
    paddingHorizontal: 10,
  },
  fieldRowFull: {
    marginBottom: 12,
    paddingHorizontal: 10,
  },
  fieldLabel: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#666666',
    width: '30%',
    marginRight: 10,
  },
  fieldLabelFull: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#666666',
    marginBottom: 5,
  },
  fieldValue: {
    fontSize: 11,
    color: '#333333',
    flex: 1,
  },
  fieldValueFull: {
    fontSize: 11,
    color: '#333333',
    lineHeight: 1.5,
    padding: 10,
    backgroundColor: '#F9F9F9',
    borderRadius: 4,
    border: '1 solid #EEEEEE',
  },
  signatureSection: {
    marginTop: 40,
    paddingTop: 20,
    borderTop: '1 solid #CCCCCC',
  },
  signaturesContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: 60,
    gap: 20,
  },
  signatureBox: {
    borderTop: '1 solid #333333',
    paddingTop: 5,
    width: '40%',
  },
  signatureText: {
    fontSize: 10,
    textAlign: 'center',
    color: '#666666',
  },
  signatureName: {
    fontSize: 11,
    textAlign: 'center',
    fontWeight: 'bold',
    color: '#333333',
    marginTop: 5,
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    textAlign: 'center',
    color: '#999999',
    fontSize: 9,
    borderTop: '1 solid #CCCCCC',
    paddingTop: 10,
  },
  badge: {
    backgroundColor: '#FFF3F8',
    color: '#E91E63',
    padding: '4 8',
    borderRadius: 4,
    fontSize: 9,
    fontWeight: 'bold',
  },
});

const PdfActionDetailDocument = ({
  config,
  author,
  generatedBy,
  logo,
}: PdfActionDetailDocumentProps) => {
  const { action, studentName, studentRun, sections } = config;

  return (
    <Document
      title={`Detalle de Acción - ${action.cod_accion}`}
      author={author}
      subject={`Acción académica registrada el ${action.fecha}`}
      keywords="CFT Maule, Alerta Temprana, Acción Académica"
      creator="Sistema Alerta Temprana CFT Maule"
      producer="Sistema Alerta Temprana CFT Maule"
    >
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.headerRow}>
            {logo && <Image src={logo} style={styles.logo} />}
            <View style={styles.headerText}>
              <Text style={styles.title}>Detalle de Acción Académica</Text>
              {studentName && (
                <Text style={styles.subtitle}>Estudiante: {studentName}</Text>
              )}
              {studentRun && (
                <Text style={styles.subtitle}>RUN: {studentRun}</Text>
              )}
              <Text style={styles.metadata}>
                Generado: {new Date().toLocaleString('es-CL')} | Código de
                Acción: {action.cod_accion}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.content}>
          <Text style={styles.sectionTitle}>Información de la Acción</Text>

          {sections.map((section, index) =>
            section.fullWidth ? (
              <View key={index} style={styles.fieldRowFull}>
                <Text style={styles.fieldLabelFull}>{section.label}:</Text>
                <Text style={styles.fieldValueFull}>{section.value}</Text>
              </View>
            ) : (
              <View key={index} style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>{section.label}:</Text>
                <Text style={styles.fieldValue}>{section.value}</Text>
              </View>
            ),
          )}
        </View>

        <View style={styles.signatureSection}>
          <View style={styles.signaturesContainer}>
            <View style={styles.signatureBox}>
              <Text style={styles.signatureText}>Firma Responsable</Text>
              <Text style={styles.signatureName}>{generatedBy}</Text>
            </View>
            <View style={styles.signatureBox}>
              <Text style={styles.signatureText}>Firma Estudiante</Text>
              <Text style={styles.signatureName}>
                {studentName || 'Estudiante'}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.footer} fixed>
          <Text
            render={({ pageNumber, totalPages }) =>
              `CFT Maule - Sistema de Alerta Temprana | Acción ${action.cod_accion} | Página ${pageNumber} de ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
};

export default PdfActionDetailDocument;
